import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, type Me } from '../api';
import { resetPendingApprovalPrompt, signedInHome, useAuth } from '../auth';

function roleHome(user: Pick<Me, 'role' | 'needsClinicOnboarding' | 'mustChangePassword'>): string {
  return signedInHome(user);
}

export type GoogleProfilePrefill = {
  email: string;
  fullName: string;
  credential: string;
  pictureUrl?: string | null;
};

function readGoogleProfile(credential: string): { email: string; fullName: string; pictureUrl: string | null } {
  const segment = credential.split('.')[1];
  if (!segment) return { email: '', fullName: '', pictureUrl: null };
  const json = JSON.parse(
    decodeURIComponent(
      [...atob(segment.replace(/-/g, '+').replace(/_/g, '/'))]
        .map(ch => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    ),
  ) as { email?: string; name?: string; picture?: string };
  const email = String(json.email ?? '').trim().toLowerCase();
  const fullName = String(json.name ?? '').trim();
  const pictureUrl = String(json.picture ?? '').trim() || null;
  return { email, fullName: fullName || email, pictureUrl };
}

export function GoogleSignIn({
  onError,
  onUnknownAccount,
}: {
  onError: (message: string) => void;
  onUnknownAccount?: (profile: GoogleProfilePrefill) => void;
}) {
  const { refetch } = useAuth();
  const navigate = useNavigate();
  const slotRef = useRef<HTMLDivElement>(null);
  const onErrorRef = useRef(onError);
  const onUnknownAccountRef = useRef(onUnknownAccount);
  onErrorRef.current = onError;
  onUnknownAccountRef.current = onUnknownAccount;
  const [enabled, setEnabled] = useState(false);
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get<{ enabled: boolean; clientId: string }>('/api/auth/google/config')
      .then(cfg => {
        if (cancelled) return;
        setEnabled(cfg.enabled);
        setClientId(cfg.clientId);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!enabled || !clientId || !slotRef.current) return;

    const handleCredential = async (credential: string) => {
      try {
        const user = await api.post<Me>('/api/auth/google', { credential });
        if (user.role === 'student' && user.approvalStatus === 'pending') {
          resetPendingApprovalPrompt(user.id);
        }
        await refetch();
        navigate(roleHome(user));
      } catch (err) {
        if (err instanceof ApiError && err.code === 'google_account_not_found') {
          if (onUnknownAccountRef.current) {
            const profile = readGoogleProfile(credential);
            if (!profile.email) {
              onErrorRef.current('Google did not return an email. Use the form instead.');
              return;
            }
            onUnknownAccountRef.current({ ...profile, credential });
            return;
          }
          onErrorRef.current('Join us first with the form, then use Google with the same email.');
          return;
        }
        onErrorRef.current(err instanceof ApiError ? err.message : 'Google sign-in failed.');
      }
    };

    const render = () => {
      const parent = slotRef.current;
      if (!parent || !window.google?.accounts?.id) return;
      parent.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: res => { void handleCredential(res.credential); },
        ux_mode: 'popup',
      });
      window.google.accounts.id.renderButton(parent, {
        theme: 'outline',
        size: 'large',
        text: onUnknownAccountRef.current ? 'signup_with' : 'continue_with',
        width: 320,
        locale: 'en',
      });
    };

    if (window.google?.accounts?.id) {
      render();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-google-gsi]');
    if (existing) {
      existing.addEventListener('load', render);
      return () => existing.removeEventListener('load', render);
    }

    // hl=en keeps the account picker / consent UI in English regardless of browser locale.
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client?hl=en';
    script.async = true;
    script.defer = true;
    script.dataset.googleGsi = '1';
    script.addEventListener('load', render);
    document.head.appendChild(script);
    return () => script.removeEventListener('load', render);
  }, [enabled, clientId, navigate, refetch]);

  if (!enabled) return null;

  return (
    <div className="mt-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-line" />
        <span className="text-xs text-ink-faint">or</span>
        <div className="h-px flex-1 bg-line" />
      </div>
      <div ref={slotRef} className="flex justify-center min-h-[44px]" />
    </div>
  );
}
